import {
  scopeRegisterFor,
  type CostScopeRegisterItem,
  type ScopeClassification,
} from "../developer-cost-basis";
import { Model } from "../model";
import type {
  ContributionTiming,
  LoadedDeveloperCostBasis,
  ModelResult,
  Scenario,
} from "../types";
import {
  NOI_ON_DEVELOPER_EQUITY_LABEL,
  NOI_ON_DEVELOPER_EQUITY_DEFINITION,
  matchedHorizonLabel,
} from "./return-labels";

export type CostScopeStatus =
  "included" | "provisional" | "tbc" | "unpriced" | "excluded" | "missing";

export type CostLoadingBasis =
  "net_trade" | "model_loading" | "model_component" | "loaded" | "unknown";

export interface BoardCostEvidenceItem {
  id: string;
  label: string;
  amount: number | null;
  areaSqm?: number | null;
  basis: CostLoadingBasis;
  status: CostScopeStatus;
  source: string | null;
  asAt?: string | null;
}

/** Presentation-only evidence. It is never included in model totals. */
export interface BoardCostEvidence {
  items: readonly BoardCostEvidenceItem[];
}

export type CostWaterfallTreatment =
  | "additive"
  | "embedded_disclosure"
  | "excluded_diagnostic"
  | "scope_disclosure"
  | "comparison_evidence";

export interface BoardCostWaterfallRow extends BoardCostEvidenceItem {
  treatment: CostWaterfallTreatment;
  rate?: number;
  scopeClassification?: ScopeClassification;
  note?: string;
}

export interface BoardCostSourceBasis {
  mode: "loaded_aggregate";
  sourceName: string;
  sourceDate: string;
  sourceBuildingAreaSqm: number;
  scenarioBuildingAreaSqm: number;
  allocationMethod: "scenario_gfa_pro_rata";
  buildingWorks: number;
  siteWorks: number;
  adoptedConstruction: number;
  gstStatus: LoadedDeveloperCostBasis["gstStatus"];
  escalationStatus: LoadedDeveloperCostBasis["escalationStatus"];
  embeddedLoadings: readonly {
    id: string;
    label: string;
    rate: number;
  }[];
}

export interface BoardCostBridge {
  adoptedTotal: number;
  rows: readonly { id: string; label: string; amount: number }[];
  totalVariance: number;
  upperBoundTotal: number;
}

export interface BoardCostSensitivity {
  id:
    | "adopted_progressive"
    | "upper_progressive"
    | "adopted_upfront"
    | "adopted_completion";
  label: string;
  lens: "counterparty_diagnostic";
  constructionCost: number;
  contributionTiming: ContributionTiming;
  contribution: number;
  rentYear1: number;
  rentPctRevenueYear1: number;
  operatorNpv: number;
  developerUngearedReturn: number;
  developerGearedReturn: number;
  yieldOnCost: number;
  grossTdc: number;
  allInCost: number;
  developerEquity: number;
  grossDebtCapacity: number;
  actualDebt: number;
  peakDebt: number;
  constructionInterest: number;
  lineFee: number;
}

export interface BoardCostViewModel {
  headline: {
    grossTdc: number;
    stabilisedYear1Noi: number;
    yieldOnCost: number;
    exitCapRate: number;
    developmentSpread: number;
  };
  funding: {
    grossTdc: number;
    allInCost: number;
    contribution: number;
    grossDebtCapacity: number;
    constructionDebt: number;
    peakDebt: number;
    developerEquity: number;
    totalSources: number;
    difference: number;
  };
  diagnostics: {
    horizon: { exitYear: number; label: string };
    matchedUngearedReturn: number;
    matchedGearedReturn: number;
    developerDebt: number;
    developerEquity: number;
    profit: number;
    marginOnCost: number;
    noiOnDeveloperEquity: {
      label: string;
      definition: string;
      value: number;
    };
  };
  sourceBasis: BoardCostSourceBasis | null;
  constructionBridge: BoardCostBridge | null;
  scopeRegister: readonly CostScopeRegisterItem[];
  sensitivities: readonly BoardCostSensitivity[];
  /** Canonical rows shared by the UI, workbook and PDF waterfall. */
  waterfallRows: readonly BoardCostWaterfallRow[];
  /** Backward-compatible alias for waterfallRows. */
  costSchedule: readonly BoardCostWaterfallRow[];
  costScheduleTotal: number;
  evidence: {
    confidence: "reconciled" | "provisional" | "incomplete";
    counts: Readonly<Record<CostScopeStatus, number>>;
    message: string;
  };
}

export const BOARD_COST_COPY = {
  yieldOnCostDefinition:
    "Yield on cost = stabilised Year-1 NOI / gross total development cost.",
  fundingDisclosure:
    "Inspired/Reddam contribution is a funding source. It replaces actual developer debt; it does not reduce gross pre-finance TDC or developer equity.",
  missingEvidence:
    "Cost evidence not available. Model totals remain visible, but scope basis and provenance are provisional until evidence is supplied.",
  diagnosticDisclosure:
    "Developer returns are counterparty diagnostics only. They do not create additional rent entitlement.",
} as const;

export const BOARD_COST_BASIS_LABELS: Readonly<
  Record<CostLoadingBasis, string>
> = {
  net_trade: "Net trade",
  model_loading: "Model loading",
  model_component: "Other model component",
  loaded: "Includes preliminaries/OH/margin",
  unknown: "Basis not evidenced",
};

export const BOARD_COST_STATUS_LABELS: Readonly<
  Record<CostScopeStatus, string>
> = {
  included: "Included",
  provisional: "Provisional",
  tbc: "TBC",
  unpriced: "Unpriced",
  excluded: "Excluded",
  missing: "Missing",
};

const STATUS_ORDER: readonly CostScopeStatus[] = [
  "included",
  "provisional",
  "tbc",
  "unpriced",
  "excluded",
  "missing",
];

/**
 * Board presentation sequence, not a substitute for the monthly cash-flow
 * timing model. It keeps acquisition, diligence and approvals ahead of the
 * construction package, then shows construction loadings and carrying costs.
 */
const DEVELOPMENT_COST_PRESENTATION_ORDER: Readonly<Record<string, number>> = {
  "land-purchase": 10,
  "stamp-duty": 20,
  "acquisition-fee": 30,
  "other-dd": 40,
  consultants: 50,
  planning: 60,
  "developer-approvals": 70,
  statutory: 80,
  "tenant-relocation": 90,
  "loaded-building-works": 100,
  "loaded-site-works": 110,
  amenities: 110,
  "embedded-preliminaries": 120,
  preliminaries: 120,
  "embedded-builder-margin": 130,
  "builder-margin": 130,
  "embedded-builder-overheads": 140,
  contingency: 150,
  "holding-interest": 160,
  "dm-fee": 170,
};

function developmentCostPresentationOrder(row: BoardCostWaterfallRow): number {
  if (row.id.startsWith("building-")) return 100;
  return DEVELOPMENT_COST_PRESENTATION_ORDER[row.id] ?? Number.MAX_SAFE_INTEGER;
}

function modelCostRows(
  scenario: Scenario,
  result: ModelResult,
): BoardCostWaterfallRow[] {
  const developer = result.developer;
  const loadedBasis = resolveLoadedCostBasis(scenario);
  const rows: BoardCostWaterfallRow[] = loadedBasis
    ? [
        {
          id: "loaded-building-works",
          label: "Adopted loaded building works",
          amount: developer.hardConstruction,
          areaSqm: loadedBasis.sourceBuildingAreaSqm,
          basis: "loaded",
          status: "included",
          source: `${loadedBasis.sourceName} / ${loadedBasis.sourceDate}`,
          treatment: "additive",
          note: "Fixed source total; scenario GFA allocation is for phasing only.",
        },
        {
          id: "loaded-site-works",
          label: "Adopted loaded site/outdoor works",
          amount: developer.siteWorksConstruction,
          basis: "loaded",
          status: "included",
          source: `${loadedBasis.sourceName} / ${loadedBasis.sourceDate}`,
          treatment: "additive",
          note: "Replaces separate amenity-construction additions.",
        },
        ...embeddedLoadingRows(loadedBasis),
      ]
    : scenario.buildings.map((building) => ({
        id: `building-${building.id}`,
        label: building.name,
        amount: building.gfaSqm * building.constructionPerSqmGfa,
        areaSqm: building.gfaSqm,
        basis: "net_trade" as const,
        status: "included" as const,
        source: "Model cost component",
        treatment: "additive" as const,
      }));
  const derived: Array<[string, string, number]> = [
    ["land-purchase", "Land purchase", developer.purchasePrice],
    ["stamp-duty", "Stamp duty", developer.stampDuty],
    ["acquisition-fee", "Acquisition fee", developer.acquisitionFee],
    ["preliminaries", "Preliminaries", developer.preliminaries],
    ["builder-margin", "Builder margin and overhead", developer.builderMargin],
    ["consultants", "Consultants", developer.consultantCosts],
    ["statutory", "Statutory costs", developer.statutoryCosts],
    [
      "contingency",
      "Construction contingency",
      developer.constructionContingency,
    ],
    ["planning", "Planning approval costs", developer.planningApprovalCosts],
    [
      "developer-approvals",
      "Developer approval share",
      developer.developerApprovalShare,
    ],
    ["holding-interest", "Holding interest", developer.holdingInterest],
    ["other-dd", "Other due diligence costs", developer.otherDdCosts],
    [
      "tenant-relocation",
      "Tenant relocation costs",
      developer.tenantRelocationCosts,
    ],
    ["amenities", "Amenity construction", developer.amenityConstruction],
    [
      "dm-fee",
      "Development management fee",
      developer.developmentManagementFee,
    ],
  ];
  const modelLoadingIds = new Set([
    "preliminaries",
    "builder-margin",
    "contingency",
  ]);
  for (const [id, label, amount] of derived) {
    if (
      loadedBasis &&
      ["preliminaries", "builder-margin", "amenities"].includes(id)
    ) {
      continue;
    }
    rows.push({
      id,
      label,
      amount,
      basis: modelLoadingIds.has(id) ? "model_loading" : "model_component",
      status: "included",
      source: "Model cost component",
      treatment: "additive",
    });
  }
  rows.sort(
    (left, right) =>
      developmentCostPresentationOrder(left) -
      developmentCostPresentationOrder(right),
  );
  rows.push(
    {
      id: "construction-interest",
      label: "Construction interest",
      amount: Math.max(0, developer.debtCostDev - developer.lineFee),
      basis: "unknown",
      status: "excluded",
      source: "Financing diagnostic - excluded from gross TDC",
      treatment: "excluded_diagnostic",
    },
    {
      id: "facility-line-fee",
      label: "Facility line fee",
      amount: developer.lineFee,
      basis: "unknown",
      status: "excluded",
      source: "Financing diagnostic - excluded from gross TDC",
      treatment: "excluded_diagnostic",
    },
    ...(loadedBasis
      ? scopeRegisterFor(loadedBasis)
          .filter((item) => item.id !== "school-buildings")
          .map(scopeRegisterWaterfallRow)
      : [
          ["vt", "Vertical transport (VT)", "tbc"],
          ["external-works", "External works", "provisional"],
          ["signage", "Signage", "unpriced"],
          ["walkways", "Walkways / other TBC scope", "tbc"],
        ].map(([id, label, status]) => ({
          id,
          label,
          amount: null,
          basis: "unknown" as const,
          status: status as CostScopeStatus,
          source: "Not separately disaggregated from model hard costs",
          treatment: "scope_disclosure" as const,
        }))),
  );
  return rows;
}

function resolveLoadedCostBasis(
  scenario: Scenario,
): LoadedDeveloperCostBasis | undefined {
  return scenario.assumptions.developerCostBasis?.mode === "loaded_aggregate"
    ? scenario.assumptions.developerCostBasis
    : undefined;
}

function embeddedLoadingRows(
  basis: LoadedDeveloperCostBasis,
): BoardCostWaterfallRow[] {
  return [
    {
      id: "embedded-preliminaries",
      label: "Preliminaries (embedded)",
      rate: basis.embeddedPreliminariesRate,
    },
    {
      id: "embedded-builder-margin",
      label: "Builder margin (embedded)",
      rate: basis.embeddedBuilderMarginRate,
    },
    {
      id: "embedded-builder-overheads",
      label: "Builder overheads (embedded)",
      rate: basis.embeddedBuilderOverheadsRate,
    },
  ].map(({ id, label, rate }) => ({
    id,
    label,
    amount: null,
    rate,
    basis: "loaded" as const,
    status: "included" as const,
    source: `${basis.sourceName} / ${basis.sourceDate}`,
    treatment: "embedded_disclosure" as const,
    note: "Disclosed within adopted loaded works; not added again.",
  }));
}

function scopeRegisterWaterfallRow(
  item: CostScopeRegisterItem,
): BoardCostWaterfallRow {
  const provisional = /provisionally/i.test(item.note);
  const status: CostScopeStatus =
    item.classification === "excluded_tbc"
      ? "tbc"
      : item.classification === "not_applicable"
        ? "excluded"
        : provisional
          ? "provisional"
          : "included";
  return {
    id: `scope-${item.id}`,
    label: item.label,
    amount: null,
    basis:
      item.classification === "embedded"
        ? "loaded"
        : item.classification === "separately_added"
          ? "model_component"
          : "unknown",
    status,
    source: item.mapping,
    treatment: "scope_disclosure",
    scopeClassification: item.classification,
    note: item.note,
  };
}

function isEvidenceItem(value: unknown): value is BoardCostEvidenceItem {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<BoardCostEvidenceItem>;
  return (
    typeof item.id === "string" &&
    typeof item.label === "string" &&
    (item.amount === null ||
      (typeof item.amount === "number" && Number.isFinite(item.amount))) &&
    (item.areaSqm === undefined ||
      item.areaSqm === null ||
      (typeof item.areaSqm === "number" && Number.isFinite(item.areaSqm))) &&
    [
      "net_trade",
      "model_loading",
      "model_component",
      "loaded",
      "unknown",
    ].includes(item.basis ?? "") &&
    STATUS_ORDER.includes(item.status as CostScopeStatus) &&
    (item.source === null || typeof item.source === "string") &&
    (item.asAt === undefined ||
      item.asAt === null ||
      typeof item.asAt === "string")
  );
}

/** Validated presentation extension retained by Scenario normalization/import. */
export function resolveBoardCostEvidence(
  scenario: Scenario,
): BoardCostEvidence | undefined {
  const candidate = (scenario as Scenario & { boardCostEvidence?: unknown })
    .boardCostEvidence;
  if (!candidate || typeof candidate !== "object") return undefined;
  const items = (candidate as { items?: unknown }).items;
  if (!Array.isArray(items) || !items.every(isEvidenceItem)) return undefined;
  return { items };
}

function missingEvidenceRow(): BoardCostWaterfallRow {
  return {
    id: "missing-current-cost-evidence",
    label: "Current builder/QS cost evidence",
    amount: null,
    basis: "unknown",
    status: "missing",
    source: null,
    treatment: "scope_disclosure",
  };
}

function evidenceSummary(
  rows: readonly BoardCostEvidenceItem[],
  evidenceItems: readonly BoardCostEvidenceItem[],
): BoardCostViewModel["evidence"] {
  const hasCurrentEvidence = evidenceItems.length > 0;
  const counts = Object.fromEntries(
    STATUS_ORDER.map((status) => [
      status,
      rows.filter((row) => row.status === status).length,
    ]),
  ) as Record<CostScopeStatus, number>;
  const incomplete = evidenceItems.some((item) =>
    ["provisional", "tbc", "unpriced", "missing"].includes(item.status),
  );
  return {
    confidence: !hasCurrentEvidence
      ? "provisional"
      : incomplete
        ? "incomplete"
        : "reconciled",
    counts,
    message: !hasCurrentEvidence
      ? BOARD_COST_COPY.missingEvidence
      : incomplete
        ? "Some cost evidence is incomplete. Review provisional, TBC, unpriced and missing rows before relying on the evidence basis."
        : "Attached cost evidence is complete for presentation review.",
  };
}

function sourceBasisView(
  scenario: Scenario,
  result: ModelResult,
): BoardCostSourceBasis | null {
  const basis = resolveLoadedCostBasis(scenario);
  if (!basis) return null;
  return {
    mode: "loaded_aggregate",
    sourceName: basis.sourceName,
    sourceDate: basis.sourceDate,
    sourceBuildingAreaSqm: basis.sourceBuildingAreaSqm,
    scenarioBuildingAreaSqm: result.campus.totalGfaSqm,
    allocationMethod: basis.allocationMethod,
    buildingWorks: basis.buildingWorks,
    siteWorks: basis.siteWorks,
    adoptedConstruction: basis.buildingWorks + basis.siteWorks,
    gstStatus: basis.gstStatus,
    escalationStatus: basis.escalationStatus,
    embeddedLoadings: [
      {
        id: "embedded-preliminaries",
        label: "Preliminaries",
        rate: basis.embeddedPreliminariesRate,
      },
      {
        id: "embedded-builder-margin",
        label: "Builder margin",
        rate: basis.embeddedBuilderMarginRate,
      },
      {
        id: "embedded-builder-overheads",
        label: "Builder overheads",
        rate: basis.embeddedBuilderOverheadsRate,
      },
    ],
  };
}

function constructionBridge(scenario: Scenario): BoardCostBridge | null {
  const basis = resolveLoadedCostBasis(scenario);
  if (!basis) return null;
  const cents = (value: number) => Math.round(value * 100) / 100;
  const adoptedTotal = cents(basis.buildingWorks + basis.siteWorks);
  const rows = [
    {
      id: "building-package-variance",
      label: "Building-package variance",
      amount: cents(basis.upperBoundBuildingWorks - basis.buildingWorks),
    },
    {
      id: "site-outdoor-variance",
      label: "Site/outdoor variance",
      amount: cents(basis.upperBoundSiteWorks - basis.siteWorks),
    },
    {
      id: "quote-line-rounding",
      label: "Quote-line rounding reconciliation",
      amount: cents(
        basis.upperBoundTotal -
          basis.upperBoundBuildingWorks -
          basis.upperBoundSiteWorks,
      ),
    },
  ] as const;
  return {
    adoptedTotal,
    rows,
    totalVariance: cents(basis.upperBoundTotal - adoptedTotal),
    upperBoundTotal: basis.upperBoundTotal,
  };
}

function sensitivityRow(
  source: Scenario,
  id: BoardCostSensitivity["id"],
  label: string,
  contributionTiming: ContributionTiming,
  buildingWorks: number,
  siteWorks: number,
): BoardCostSensitivity {
  const scenario = structuredClone(source);
  const basis = resolveLoadedCostBasis(scenario);
  if (!basis) {
    throw new Error("Loaded cost sensitivity requires a loaded cost basis.");
  }
  basis.buildingWorks = buildingWorks;
  basis.siteWorks = siteWorks;
  scenario.assumptions.contributionTiming = contributionTiming;
  const result = Model.compute(scenario);
  const developer = result.developer;
  const year1 = result.years[0];
  return {
    id,
    label,
    lens: "counterparty_diagnostic",
    constructionCost: buildingWorks + siteWorks,
    contributionTiming,
    contribution: developer.operatorContributionApplied,
    rentYear1: (year1?.rentGross ?? 0) + (year1?.amenityRent ?? 0),
    rentPctRevenueYear1: result.kpis.rentPctRevenueYear1,
    operatorNpv: result.kpis.reddamNpv,
    developerUngearedReturn: result.kpis.developerPrimaryIrr,
    developerGearedReturn: result.kpis.developerPrimaryGearedIrr,
    yieldOnCost: developer.yieldOnCost,
    grossTdc: developer.totalCost,
    allInCost: developer.allInCost,
    developerEquity: developer.equityRequired,
    grossDebtCapacity: developer.grossFacilityLimitDev,
    actualDebt: developer.facilityLimitDev,
    peakDebt: developer.peakDebtDev,
    constructionInterest: developer.constructionInterest,
    lineFee: developer.lineFee,
  };
}

function fixedSensitivities(
  scenario: Scenario,
): readonly BoardCostSensitivity[] {
  const basis = resolveLoadedCostBasis(scenario);
  if (!basis) return [];
  const exactUpperSiteWorks =
    basis.upperBoundTotal - basis.upperBoundBuildingWorks;
  return [
    sensitivityRow(
      scenario,
      "adopted_progressive",
      "Adopted cost + progressive contribution",
      "progressive",
      basis.buildingWorks,
      basis.siteWorks,
    ),
    sensitivityRow(
      scenario,
      "upper_progressive",
      "Upper-bound cost + progressive contribution",
      "progressive",
      basis.upperBoundBuildingWorks,
      exactUpperSiteWorks,
    ),
    sensitivityRow(
      scenario,
      "adopted_upfront",
      "Adopted cost + contribution used first",
      "upfront",
      basis.buildingWorks,
      basis.siteWorks,
    ),
    sensitivityRow(
      scenario,
      "adopted_completion",
      "Adopted cost + contribution received at completion",
      "completion",
      basis.buildingWorks,
      basis.siteWorks,
    ),
  ];
}

export function buildBoardCostViewModel(
  scenario: Scenario,
  result: ModelResult,
  evidence?: BoardCostEvidence,
): BoardCostViewModel {
  evidence ??= resolveBoardCostEvidence(scenario);
  const developer = result.developer;
  const contribution = developer.operatorContributionApplied;
  const totalSources =
    contribution + developer.facilityLimitDev + developer.equityRequired;
  const exitYear =
    scenario.assumptions.developerExitYear ?? scenario.assumptions.modelYears;
  const loadedBasis = resolveLoadedCostBasis(scenario);
  // Builder quotes remain comparison evidence unless their itemised rows are
  // explicitly mapped into this schedule. Their mere presence must not imply
  // that the model cost schedule is reconciled to quote rows.
  const hasCurrentEvidence =
    Boolean(loadedBasis) || (evidence?.items.length ?? 0) > 0;
  const costSchedule = [
    ...modelCostRows(scenario, result),
    ...(evidence?.items ?? []).map((item): BoardCostWaterfallRow => ({
      ...item,
      treatment: "comparison_evidence",
    })),
    ...(hasCurrentEvidence ? [] : [missingEvidenceRow()]),
  ];
  const loadedEvidenceItems = loadedBasis
    ? costSchedule.filter((row) =>
        ["embedded_disclosure", "scope_disclosure"].includes(row.treatment),
      )
    : [];

  return {
    headline: {
      grossTdc: developer.totalCost,
      stabilisedYear1Noi: developer.noi,
      yieldOnCost: developer.yieldOnCost,
      exitCapRate: scenario.assumptions.capRate,
      developmentSpread: developer.yieldOnCost - scenario.assumptions.capRate,
    },
    funding: {
      grossTdc: developer.totalCost,
      allInCost: developer.allInCost,
      contribution,
      grossDebtCapacity: developer.grossFacilityLimitDev,
      constructionDebt: developer.facilityLimitDev,
      peakDebt: developer.peakDebtDev,
      developerEquity: developer.equityRequired,
      totalSources,
      difference: developer.fundingResidual,
    },
    diagnostics: {
      horizon: {
        exitYear,
        // Hub call (loop#002 review): constructed by the same helper every other surface uses,
        // so the wording can never drift from matchedHorizonLabel/rentBridgeExitBasisLabel.
        label: matchedHorizonLabel(exitYear, scenario.assumptions.modelYears),
      },
      matchedUngearedReturn: result.kpis.developerPrimaryIrr,
      matchedGearedReturn: result.kpis.developerPrimaryGearedIrr,
      developerDebt: developer.facilityLimitDev,
      developerEquity: developer.equityRequired,
      profit: developer.profit,
      marginOnCost: developer.marginOnCost,
      noiOnDeveloperEquity: {
        label: NOI_ON_DEVELOPER_EQUITY_LABEL,
        definition: NOI_ON_DEVELOPER_EQUITY_DEFINITION,
        value: developer.yieldOnCash,
      },
    },
    sourceBasis: sourceBasisView(scenario, result),
    constructionBridge: constructionBridge(scenario),
    scopeRegister: scopeRegisterFor(loadedBasis),
    sensitivities: fixedSensitivities(scenario),
    waterfallRows: costSchedule,
    costSchedule,
    costScheduleTotal: developer.totalCost,
    evidence: evidenceSummary(costSchedule, [
      ...loadedEvidenceItems,
      ...(evidence?.items ?? []),
    ]),
  };
}
